import { useEffect, useState } from 'react';

import { cn } from '~/lib/cn';

import { Icon } from '../Icon';
import { IconButton } from '../IconButton';

interface ErrorToastProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string | React.ReactNode;
  durationInMs?: number;
  className?: string;
}

export function ErrorToast({
  isOpen,
  onClose,
  title,
  description,
  durationInMs = 5000,
  className,
}: ErrorToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);

      const timer = setTimeout(() => {
        onClose();
      }, durationInMs);

      return () => {
        clearTimeout(timer);
      };
    }

    setIsAnimating(false);

    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [isOpen, onClose, durationInMs]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed right-lg top-lg max-w-[18.75rem] rounded-sm border-px p-md transition-opacity duration-150 bg-level-05 border-emphasis-subtle text-default',
        isAnimating ? 'opacity-full' : 'opacity-none',
        className,
      )}
    >
      <div className="flex flex-col gap-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-sm">
            <Icon type="octagon-xmark" className="text-error-default" />
            <div className="text-headline-sm font-bold">{title}</div>
          </div>
          <IconButton iconType="xmark" onClick={onClose} aria-label="Close" />
        </div>

        {description ? (
          <div className="text-body-sm font-normal">{description}</div>
        ) : null}
      </div>
    </div>
  );
}

ErrorToast.displayName = 'ErrorToast';
